/*
  La Sura - <lasura-postcode-check>, "Check your postcode" on the area pages
  (sections/lasura-area-delivery.liquid). Matches the postcode's first half (BR1 from BR1 3HY)
  against every delivery area (data-zones, from snippets/lasura-postcode-zones.liquid) and says
  which area it's in and the fee, a link to that area's page if it's another one, or that we don't
  deliver there yet. The same matching as the basket's day card (lasura-basket-when-card.js).
  In an area, the words are the one postcode result (assets/lasura-delivery-words.js placeLine(),
  redesign L7): "SE6 is in Catford. Delivery is £8, free on orders over {threshold}." The per-section
  "In an area" settings are retired.
  Also the menus' check (snippets/lasura-postcode-form.liquid), which reads the areas from one
  shared script on the page (data-zones-from) instead of its own attribute. Every check is
  remembered (assets/lasura-postcode.js) so the rest of the site knows the district.

  2026-10-25 (Matt: collection for Greater London only, so nobody far away orders by mistake):
  - An area whose zone is "Collect" is collection only: it has a page but no delivery, so the
    check names it and says collect from Brockley (data-text-collect), with the link to its page.
  - A district on data-never-deliver (La Sura brand > Never deliver to: the congestion charge zone
    and tolled tunnels) is never treated as a delivery area, even if one lists it.
  - A postcode in no area is told it can collect (data-text-no) only when its postcode area (SE,
    BR) is in data-collect-areas, from La Sura brand > Collection postcode areas. Anywhere else
    gets data-text-far. An empty list keeps the old behaviour: collection offered everywhere.
*/
import { canCollect, isCollectOnly, neverDeliver } from '@theme/lasura-postcode-areas';
import { lookup, remember } from '@theme/lasura-postcode';
import { facts, placeLine, paintPlace } from '@theme/lasura-delivery-words';

const outward = (value) => {
  const m = String(value || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})?$/);
  return m ? m[1] : '';
};

const fill = (text, values) => (text || '').replace(/\{(\w+)\}/g, (all, k) => (k in values ? values[k] : all));

class LasuraPostcodeCheck extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input');
    this.out = this.querySelector('[data-postcode-out]');
    this.zones = null;
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.check();
    });
  }

  // Read on first check, not on connect, so the menus' shared script (data-zones-from, 2026-10-25)
  // is found even if this element upgrades before that script is in the page.
  readZones() {
    const shared = this.dataset.zonesFrom ? document.getElementById(this.dataset.zonesFrom) : null;
    try {
      return JSON.parse((shared ? shared.textContent : this.dataset.zones) || '[]');
    } catch {
      return [];
    }
  }

  check() {
    if (!this.zones || this.zones.length === 0) this.zones = this.readZones();
    const d = this.dataset;
    const code = outward(this.input?.value);
    const listed = code ? this.zones.find((z) => (z.c || []).some((c) => outward(c) === code)) : null;
    // A district on "Never deliver to" is never a delivery zone, whatever an area lists (2026-10-25).
    const zone = listed && !isCollectOnly(listed) && neverDeliver(code, d.neverDeliver) ? null : listed;
    // Remembered across the site (2026-10-29 redesign, assets/lasura-postcode.js).
    if (code) remember(lookup(this.input?.value, this.zones, d.neverDeliver), d.source || (this.dataset.zonesFrom ? 'menu' : 'area'));
    if (!this.out) return;
    this.out.classList.remove('is-ok', 'is-no');
    this.out.replaceChildren();
    if (!code) {
      this.out.textContent = d.textBad || '';
      this.out.classList.add('is-no');
      return;
    }
    if (!zone) {
      const far = !canCollect(code, d.collectAreas) && d.textFar;
      this.out.textContent = fill(far ? d.textFar : d.textNo, { code });
      this.out.classList.add('is-no');
      return;
    }
    const collectOnly = isCollectOnly(zone);
    this.out.classList.add(collectOnly ? 'is-no' : 'is-ok');
    const here = zone.u && location.pathname.replace(/\/$/, '').endsWith(zone.u.replace(/\/$/, ''));
    // In an area: the one postcode result every check gives (redesign L7, board FreeDelivery card 5).
    if (collectOnly) this.out.append(fill(d.textCollect || d.textNo, { code, area: zone.n }));
    else paintPlace(this.out, placeLine(lookup(code, this.zones, d.neverDeliver), facts()));
    const linkText = collectOnly ? d.textCollectLink : d.textOther;
    if (!here && zone.u && linkText) {
      const link = document.createElement('a');
      link.href = zone.u;
      link.textContent = fill(linkText, { area: zone.n });
      this.out.append(' ', link);
    }
  }
}

if (!customElements.get('lasura-postcode-check')) customElements.define('lasura-postcode-check', LasuraPostcodeCheck);
